import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { FilterCondition } from '@/components/customTable/types';
import { contactsKeys } from './use-contacts';
import { campaignsKeys } from './use-campaigns';

export interface Group {
  id: string;
  name: string;
  filter: FilterCondition[];
  isStatic: boolean;
}

export interface GroupsResponse {
  data: Group[];
  total: number;
}

export interface GroupFormData {
  name: string;
  isStatic?: boolean;
  filter: {field?: string, value: string, operator: string}[];
}

export const groupsKeys = {
  all: ['groups'] as const,
  lists: () => [...groupsKeys.all, 'list'] as const,
  list: (query?: string) => [...groupsKeys.lists(), { query }] as const,
  details: () => [...groupsKeys.all, 'detail'] as const,
  detail: (id: string) => [...groupsKeys.details(), id] as const,
};

export function useGroupsList(query?: string) {
  return useQuery({
    queryKey: groupsKeys.list(query),
    queryFn: async (): Promise<GroupsResponse> => {
      const queryString = query ? `?${query}` : '';
      const response = await apiClient.get(`/crm/groups${queryString}`);
      return response.data;
    },
  });
}

export function useGroup(id: string) {
  return useQuery({
    queryKey: groupsKeys.detail(id),
    queryFn: async (): Promise<Group> => {
      const response = await apiClient.get(`/crm/groups/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GroupFormData): Promise<Group> => {
      const response = await apiClient.post('/crm/groups', {
        name: data.name,
        filter: data.filter,
        isStatic: data.isStatic,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() });
      toast.success('Group created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: GroupFormData }): Promise<Group> => {
      const response = await apiClient.put(`/crm/groups/${id}`, {
        name: data.name,
        filter: data.filter,
        isStatic: data.isStatic,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(groupsKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() });
      queryClient.removeQueries({ queryKey: contactsKeys.all });
      queryClient.removeQueries({ queryKey: campaignsKeys.all });
      toast.success('Group updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/crm/groups/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: groupsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() });
      queryClient.removeQueries({ queryKey: contactsKeys.all });
      queryClient.removeQueries({ queryKey: campaignsKeys.all });
      toast.success('Group deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error('Something went wrong!');
    },
  });
}
